import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppTooltip from '@tenant/core/AppTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IntlMessages from '@tenant/utility/IntlMessages';
import { ROUTE_STRING } from 'constants/RouteString';
import { Formik } from 'formik';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { LoadingButton } from '@mui/lab';
import validationSchema from '../../redux/slices/appInstance/constants/validationSchema';
import { grey } from '@mui/material/colors';
import useAddAppInstance from 'redux/slices/appInstance/hooks/useAddAppInstance';
import AppPackageSelect from '@tenant/core/App/Select/AppPackageSelect';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import AddEnvironmentDrawer from './components/Add/AddEnvironmentDrawer';
import useModal from '@tenant/utility/hooks/useModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MarketplaceAppSelect from '@tenant/core/App/Select/MarketplaceAppSelect';
import DockerAppTypes from 'constants/DockerAppTypes';
import AppsContainer from '@tenant/core/AppsContainer';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';

const Add = () => {
  const { visible, onClose, onShow } = useModal();
  const { 
    visible: confirmModalVisible, 
    onClose: onConfirmModalClose, 
    onShow: onConfirmModalShow 
  } = useModal();
  const [containerApp, setContainerApp] = useState();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const navigate = useNavigate();
  const onClickBackButton = () => {
    navigate(ROUTE_STRING.APP_INSTANCE.LIST);
  };
  const addItem = useAddAppInstance();
  const uniqueId = useId();

  const handleSubmit = (data) => {
    const isValid = !data?.AppPackage?.AppComponents?.some((e) => !e.isValid);

    if (!isValid) return;

    // Store form data and show confirmation modal
    setPendingFormData(data);
    setIsAcknowledged(false);
    onConfirmModalShow();
  };

  const handleConfirmSubmit = () => {
    if (!isAcknowledged || !pendingFormData) return;

    const convertData = {
      ...pendingFormData,
      RequiredEnviromentVariables: undefined,
      AppPackage: undefined,
      AppModels: undefined,
      AppPackageId: pendingFormData?.AppPackage?.Id,
      AppIds: pendingFormData?.AppModels?.map((e) => e.Id),
      ContainerApps: pendingFormData?.AppPackage?.AppComponents?.map((e) => ({
        Cron: e.Cron ?? undefined,
        ComponentName: e.Name,
        EnviromentVariables: e.Environments.reduce((acc, { key, value }) => {
          acc[key] = value;
          return acc;
        }, {}),
      })),
    };
    
    addItem.mutate(convertData, {
      onSuccess(result) {
        if (result?.data?.Id)
          navigate(
            ROUTE_STRING.APP_INSTANCE.VIEW.replace(':id', result?.data?.Id),
          );
      },
    });
    
    onConfirmModalClose();
  };

  const handleConfirmModalClose = () => {
    setIsAcknowledged(false);
    setPendingFormData(null);
    onConfirmModalClose();
  };

  const handleShow = (item) => {
    setContainerApp({
      ...item,
    });
    onShow();
  };

  const handleClose = () => {
    setContainerApp(null);
    onClose();
  };

  const columns = [
    {
      field: 'Name',
      headerName: 'Name',
      width: 300,
      sortable: true,
      flex: 1,
    },
    {
      field: 'isValid',
      headerName: '',
      width: 100,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <>
          {params.value ? (
            <Tooltip title='Valid'>
              <CheckCircleIcon color='success' />
            </Tooltip>
          ) : (
            <Tooltip title='Invalid'>
              <CancelIcon color='error' />
            </Tooltip>
          )}
        </>
      ),
    },
    {
      field: 'Action',
      headerName: 'Environments',
      width: 125,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <IconButton onClick={() => handleShow(params.row)}>
          <AddIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Library</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.APP_INSTANCE.LIST}>App Instances</Link>
          </Typography>
          <Typography color='text.primary'>Add</Typography>
        </Breadcrumbs>
      }
    >
      <AppsHeader>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              gap: 4,
            }}
            component='span'
            mr={{ xs: 2, sm: 4 }}
          >
            <AppTooltip title={<IntlMessages id='common.back' />}>
              <ArrowBackIcon
                sx={{
                  color: 'text.secondary',
                }}
                onClick={onClickBackButton}
              />
            </AppTooltip>
          </Box>
        </Box>
      </AppsHeader>

      <Box p={4}>
        <Formik
          enableReinitialize
          validateOnChange={true}
          validateOnBlur={true}
          initialValues={{
            CpuAndMemory: 1,
            RequiredEnviromentVariables: [],
            DomainPrefix: 'lan-test',
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({
            submitForm,
            values,
            touched,
            setFieldValue,
            errors,
            setFieldTouched,
          }) => (
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  p: 4,
                  ml: -4,
                  mr: -4,
                }}
              >
                <Grid container spacing={5}>
                  <Grid item xs={12} sm={10}>
                    <Box
                      component='p'
                      color='text.primary'
                      fontSize={14}
                      mb={2}
                    >
                      Name
                      <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                        *
                      </Box>
                    </Box>
                    <TextField
                      fullWidth
                      value={values.Name ?? ''}
                      error={touched.Name && !!errors.Name}
                      helperText={touched.Name ? errors.Name : ''}
                      placeholder='E.g. joblogic-app-instance'
                      onChange={(e) => {
                        setFieldTouched('Name', true);
                        setFieldValue('Name', e.target.value, true);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Box
                      component='p'
                      color='text.primary'
                      fontSize={14}
                      mb={2}
                    >
                      Cpu And Memory
                      <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                        *
                      </Box>
                    </Box>
                    <TextField
                      disabled
                      fullWidth
                      type='number'
                      value={values.CpuAndMemory ?? ''}
                      error={!!errors.CpuAndMemory}
                      helperText={errors.CpuAndMemory}
                      sx={{ '& .Mui-disabled': { background: grey[50] } }}
                      onChange={(e) =>
                        setFieldValue('CpuAndMemory', e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      component='p'
                      color='text.primary'
                      fontSize={14}
                      mb={2}
                    >
                      Marketplace Apps
                    </Box>
                    <MarketplaceAppSelect
                      isMultiple
                      value={values.AppModels ?? []}
                      error={!!errors.AppModels}
                      onChange={(e, value) => {
                        setFieldValue('AppModels', value);
                        setFieldValue('AppPackage', null);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      component='p'
                      color='text.primary'
                      fontSize={14}
                      mb={2}
                    >
                      App Package
                      <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                        *
                      </Box>
                    </Box>
                    <AppPackageSelect
                      key={values.AppModels}
                      value={values.AppPackage ?? null}
                      error={touched.AppPackage && !!errors.AppPackage}
                      helperText={touched.AppPackage ? errors.AppPackage : ''}
                      queryKey={JSON.stringify({
                        uniqueId,
                        appIds: values.AppModels?.map((e) => e.Id),
                      })}
                      params={{ appIds: values.AppModels?.map((e) => e.Id) }}
                      onChange={(e, value) => {
                        setFieldTouched('AppPackage', true);
                        setFieldValue(
                          'AppPackage',
                          value
                            ? {
                                ...value,
                                AppComponents: value?.AppComponents?.map(
                                  (app) => ({
                                    ...app,
                                    isValid: !DockerAppTypes.find(
                                      (e) => e.Value === app.DeploymentType,
                                    ).IsCron,
                                    Environments:
                                      app?.RequiredEnviromentVariables
                                        ? app?.RequiredEnviromentVariables?.split(
                                            ',',
                                          ).map((e) => ({
                                            id: e,
                                            key: e,
                                            value: '',
                                          }))
                                        : [],
                                  }),
                                ),
                              }
                            : null,
                          true,
                        );
                      }}
                    />
                  </Grid>

                  {values?.AppPackage?.AppComponents?.length && (
                    <Grid item xs={12} sm={12}>
                      <Box
                        component='p'
                        color='text.primary'
                        fontSize={14}
                        mt={3}
                        mb={2}
                      >
                        Container Apps
                      </Box>

                      <Box style={{ height: 'auto', width: '100%' }}>
                        <DataGrid
                          hideFooter
                          autoHeight
                          disableColumnSelector
                          disableColumnMenu
                          rows={values?.AppPackage?.AppComponents ?? []}
                          columns={columns}
                          getRowId={(row) => row.Id}
                        />
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={2}>
                    <FormControl >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.AddToVnet ?? false}
                            onChange={async (e) => {
                              setFieldValue('AddToVnet', e.target.checked);
                            }} />
                        }
                        label=" Add to VNET"
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                {!!containerApp && (
                  <AddEnvironmentDrawer
                    visible={visible}
                    handleClose={handleClose}
                    title={containerApp.Name}
                    appType={containerApp.DeploymentType}
                    requiredEnviromentVariables={
                      containerApp.Environments?.map((e) => ({
                        ...e,
                        isValid: e.isValid ?? false,
                        isRequired: e.isRequired ?? false,
                      })) ?? []
                    }
                    setFieldValue={setFieldValue}
                    cron={containerApp?.Cron}
                    setContainerApps={({ environments, cron, isValid }) => {
                      const newAppPackage = {
                        ...values?.AppPackage,
                        Cron: cron,
                        AppComponents: values?.AppPackage?.AppComponents?.map(
                          (e) =>
                            e.Id === containerApp?.Id
                              ? {
                                  ...e,
                                  Environments: environments,
                                  Cron: cron,
                                  isValid,
                                }
                              : e,
                        ),
                      };

                      setFieldValue('AppPackage', newAppPackage);
                    }}
                  />
                )}
              </Box>

              <FooterWrapper
                className='footer'
                sx={{
                  margin: '20px -20px -16px -20px',
                  backgroundColor: '#f4f7fe',
                  height: '50px',
                }}
              >
                <Stack
                  direction='row'
                  justifyContent='end'
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    height: '100%',
                    p: 2,
                  }}
                >
                  <Button
                    variant='contained'
                    color='secondary'
                    disabled={addItem.isPending}
                    onClick={onClickBackButton}
                    sx={{ mr: 3, minWidth: 80 }}
                  >
                    Cancel
                  </Button>

                  <LoadingButton
                    variant='contained'
                    loading={addItem.isPending}
                    sx={{ mr: 3, minWidth: 80 }}
                    onClick={() => {
                      submitForm();
                    }}
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </FooterWrapper>
            </Box>
          )}
        </Formik>
      </Box>

      {/* Deployment Guidelines Confirmation Modal */}
      <Dialog
        open={confirmModalVisible}
        onClose={handleConfirmModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box>
            <Typography variant="h5" component="div" fontWeight="bold" color="text.primary">
              Critical Deployment Requirements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mandatory compliance for production deployments
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4 }}>
          <Box 
            sx={{ 
              mb: 4, 
              p: 3, 
              backgroundColor: 'error.light', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'error.main'
            }}
          >
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'error.contrastText', fontWeight: 600 }}>
              IMPORTANT: The following deployment requirements are mandatory for all Container App deployments. Non-compliance may result in security vulnerabilities, performance issues, or deployment failures.
            </Typography>
          </Box>
          
          <Stack spacing={4}>
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  1
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'primary.main' }}>
                    Security Isolation: Separate Webhook Listeners from Frontend APIs
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Webhook listeners must be deployed in dedicated Container Apps, isolated from APIs serving frontend clients. This separation is mandatory to implement proper security configurations including IP whitelisting and authentication controls, which cannot be effectively applied in shared container environments.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'warning.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  2
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'warning.main' }}>
                    Background Processing: Container App Jobs Required
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    All scheduled, timed, or background processing workloads must be deployed as Container App Jobs. Standard Container Apps are designed for HTTP-triggered scaling and will terminate during idle periods, which will cause critical failures in background operations and scheduled tasks.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  3
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'success.main' }}>
                    Naming Standards: Organizational Compliance Required
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    All Container Apps and related resources must adhere to established organizational naming conventions. Use clear, descriptive, and consistent names to ensure operational maintainability, compliance auditing, and effective resource management across the enterprise environment.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'warning.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  4
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'warning.main' }}>
                    Performance Optimization: Concurrency Configuration
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Applications handling lightweight or high-frequency requests must have properly configured concurrency limits. For frontend APIs and similar workloads, configure maximum concurrent request limits appropriately (typically 500-1000) to ensure optimal resource utilization and system performance under load.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  5
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'error.main' }}>
                    Resource Management: Immediate Cleanup Required
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Temporary, test, or unused resources must be deleted immediately upon completion of evaluation activities. Failure to remove unused resources will result in unnecessary operational costs, environmental pollution, and potential resource conflicts that may impact production systems.
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'background.paper', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'info.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  6
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold', color: 'info.main' }}>
                    API Access Policy: Backend Proxy Mandatory
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                    Direct frontend access to Marketplace Internal APIs is strictly prohibited and will be blocked. All frontend applications must route requests through designated backend services that act as secure proxies. This policy will be enforced through IP-based access restrictions effective immediately.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Stack>
          
          <Box 
            sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: '1px solid', 
              borderColor: 'divider'
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAcknowledged}
                  onChange={(e) => setIsAcknowledged(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  I acknowledge and agree to comply with all deployment requirements listed above
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleConfirmModalClose}
            variant="outlined"
            color="primary"
            size="large"
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={!isAcknowledged}
            loading={addItem.isPending}
            size="large"
            sx={{ minWidth: 160 }}
          >
            Confirm & Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </AppsContainer>
  );
};

export default Add;
